import { useCallback, useEffect, useState } from "react";

import {
  Close as CloseIcon,
  Compress as CompressIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { postSurveyCondensed } from "@rubi/api-generated/clientRs/sdk.gen";
import type { SurveyVersionOverview } from "@rubi/api-generated/clientRs/types.gen";
import { toast } from "@rubi/ui";

import { useFormBuilderContext } from "@/components/online-reporting/form-management/FormContextProvider";
import {
  extractLocaleStringFromField,
  formatVersionNumber,
} from "@/components/online-reporting/form-management/utils";
import { ILocaleObject } from "@/components/online-reporting/types";

type ViewMode = "select" | "processing" | "results";

interface CondenseResult {
  surveyId: string;
  surveyName: string;
  success: boolean;
  error?: string;
}

interface CondenseAllModalProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  onCondenseSuccess: () => void;
}

/**
 * Gets the survey title from a SurveyVersionOverview
 */
const getSurveyTitle = (survey: SurveyVersionOverview): string => {
  const version = survey.active_version || survey.draft_version;
  if (!version) return "Untitled Survey";

  const config = version.configuration as { title?: string | ILocaleObject };
  const title = config?.title;

  if (title) {
    return (
      extractLocaleStringFromField(title as string | ILocaleObject) ||
      "Untitled Survey"
    );
  }
  return "Untitled Survey";
};

export const CondenseAllModal: React.FC<CondenseAllModalProps> = ({
  open,
  onClose,
  orgId,
  onCondenseSuccess,
}) => {
  const { surveysWithActiveVersions } = useFormBuilderContext();

  const [viewMode, setViewMode] = useState<ViewMode>("select");
  const [selectedSurveyIds, setSelectedSurveyIds] = useState<Set<string>>(
    new Set(),
  );
  const [processProgress, setProcessProgress] = useState({
    completed: 0,
    total: 0,
  });
  const [condenseResults, setCondenseResults] = useState<CondenseResult[]>([]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setViewMode("select");
      setSelectedSurveyIds(new Set());
      setProcessProgress({ completed: 0, total: 0 });
      setCondenseResults([]);
    }
  }, [open]);

  const handleToggleSelection = (surveyId: string) => {
    const newSelection = new Set(selectedSurveyIds);
    if (newSelection.has(surveyId)) {
      newSelection.delete(surveyId);
    } else {
      newSelection.add(surveyId);
    }
    setSelectedSurveyIds(newSelection);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSurveyIds(
        new Set(surveysWithActiveVersions.map((s) => s.parent_survey_id)),
      );
    } else {
      setSelectedSurveyIds(new Set());
    }
  };

  const handleCondenseAll = useCallback(async () => {
    if (selectedSurveyIds.size === 0) return;

    const selectedSurveys = surveysWithActiveVersions.filter((s) =>
      selectedSurveyIds.has(s.parent_survey_id),
    );

    setViewMode("processing");
    setProcessProgress({ completed: 0, total: selectedSurveys.length });
    setCondenseResults([]);

    const results: CondenseResult[] = [];

    for (let i = 0; i < selectedSurveys.length; i++) {
      const survey = selectedSurveys[i];
      const surveyName = getSurveyTitle(survey);

      try {
        const response = await postSurveyCondensed({
          path: {
            org_id: orgId,
            survey_id: survey.parent_survey_id,
          },
        });

        if (response.data?.success) {
          results.push({
            surveyId: survey.parent_survey_id,
            surveyName,
            success: true,
          });
        } else {
          results.push({
            surveyId: survey.parent_survey_id,
            surveyName,
            success: false,
            error: "Generation returned unsuccessful",
          });
        }
      } catch (error: any) {
        console.error(
          `Error generating condensed form for survey ${survey.parent_survey_id}:`,
          error,
        );
        const errorMessage =
          error?.response?.data?.console_msg ||
          "Failed to generate condensed form";
        results.push({
          surveyId: survey.parent_survey_id,
          surveyName,
          success: false,
          error: errorMessage,
        });
      }

      setCondenseResults([...results]);
      setProcessProgress({ completed: i + 1, total: selectedSurveys.length });
    }

    setViewMode("results");

    const successCount = results.filter((r) => r.success).length;
    const failCount = results.filter((r) => !r.success).length;

    if (failCount === 0) {
      toast.success(
        `Successfully generated condensed forms for ${successCount} survey(s)`,
      );
    } else if (successCount > 0) {
      toast.warning(
        `Generated ${successCount} condensed form(s), ${failCount} failed`,
      );
    } else {
      toast.error("Failed to generate condensed forms");
    }
  }, [selectedSurveyIds, surveysWithActiveVersions, orgId]);

  const handleDone = () => {
    onCondenseSuccess();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            <CompressIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Generate Condensed Forms
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ minHeight: 400 }}>
        {/* Empty State */}
        {surveysWithActiveVersions.length === 0 && viewMode === "select" && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height={300}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No surveys available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create some surveys first before generating condensed forms.
            </Typography>
          </Box>
        )}

        {/* Select View */}
        {surveysWithActiveVersions.length > 0 && viewMode === "select" && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">
                {surveysWithActiveVersions.length} Survey
                {surveysWithActiveVersions.length !== 1 ? "s" : ""} Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedSurveyIds.size} of {surveysWithActiveVersions.length}{" "}
                selected
              </Typography>
            </Box>

            <TableContainer sx={{ maxHeight: 300 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedSurveyIds.size > 0 &&
                          selectedSurveyIds.size <
                            surveysWithActiveVersions.length
                        }
                        checked={
                          surveysWithActiveVersions.length > 0 &&
                          selectedSurveyIds.size ===
                            surveysWithActiveVersions.length
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>Survey Name</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {surveysWithActiveVersions.map((survey) => (
                    <TableRow
                      key={survey.parent_survey_id}
                      hover
                      onClick={() =>
                        handleToggleSelection(survey.parent_survey_id)
                      }
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedSurveyIds.has(
                            survey.parent_survey_id,
                          )}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() =>
                            handleToggleSelection(survey.parent_survey_id)
                          }
                        />
                      </TableCell>
                      <TableCell>{getSurveyTitle(survey)}</TableCell>
                      <TableCell>
                        {survey.active_version ? (
                          <Chip
                            label={`v${formatVersionNumber(survey.active_version)}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Chip label="Draft" size="small" color="warning" />
                        )}
                      </TableCell>
                      <TableCell>
                        {survey.active_version ? (
                          survey.is_published ? (
                            <Chip
                              label="Published"
                              size="small"
                              color="success"
                            />
                          ) : (
                            <Chip label="Active" size="small" color="info" />
                          )
                        ) : (
                          <Chip
                            label="Draft Only"
                            size="small"
                            color="warning"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Alert severity="info" sx={{ mt: 3 }}>
              Condensed forms will be generated for the selected surveys. This
              operates on the draft version - if no draft exists, one will be
              created from the active version. Existing condensed forms will be
              overwritten.
            </Alert>
          </Box>
        )}

        {/* Processing View */}
        {viewMode === "processing" && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Generating Condensed Forms...
            </Typography>

            <Box sx={{ mb: 3 }}>
              <LinearProgress
                variant="determinate"
                value={
                  (processProgress.completed / processProgress.total) * 100
                }
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, textAlign: "center" }}
              >
                Processing survey {processProgress.completed} of{" "}
                {processProgress.total}...
              </Typography>
            </Box>

            {condenseResults.length > 0 && (
              <TableContainer sx={{ maxHeight: 250 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Survey</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {condenseResults.map((result) => (
                      <TableRow key={result.surveyId}>
                        <TableCell>{result.surveyName}</TableCell>
                        <TableCell>
                          {result.success ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Generated"
                              color="success"
                              size="small"
                            />
                          ) : (
                            <Chip
                              icon={<ErrorIcon />}
                              label="Failed"
                              color="error"
                              size="small"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Results View */}
        {viewMode === "results" && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Generation Results
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${condenseResults.filter((r) => r.success).length} Succeeded`}
                color="success"
              />
              <Chip
                icon={<ErrorIcon />}
                label={`${condenseResults.filter((r) => !r.success).length} Failed`}
                color={
                  condenseResults.filter((r) => !r.success).length > 0
                    ? "error"
                    : "default"
                }
              />
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Survey Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {condenseResults.map((result) => (
                    <TableRow key={result.surveyId}>
                      <TableCell>{result.surveyName}</TableCell>
                      <TableCell>
                        {result.success ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Success"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<ErrorIcon />}
                            label="Failed"
                            color="error"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {result.error ? (
                          <Typography variant="body2" color="error">
                            {result.error}
                          </Typography>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {viewMode === "select" && surveysWithActiveVersions.length > 0 && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCondenseAll}
              disabled={selectedSurveyIds.size === 0}
              startIcon={<CompressIcon />}
            >
              Generate {selectedSurveyIds.size} Condensed Form
              {selectedSurveyIds.size !== 1 ? "s" : ""}
            </Button>
          </>
        )}

        {viewMode === "select" && surveysWithActiveVersions.length === 0 && (
          <Button onClick={onClose}>Close</Button>
        )}

        {viewMode === "processing" && (
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Please wait...
          </Typography>
        )}

        {viewMode === "results" && (
          <Button variant="contained" onClick={handleDone}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
